import { memo, useMemo } from 'react'
import { Box } from '@mui/material'
import { Contact } from '@weblite/ts-action/contact'
import { FullPageListSkeleton } from '$common/full-page-list-skeleton'
import { SearchedUserListItemWithStatus } from '$common/room-list-search/searched-user-list-item-with-status'
import { SearchedHallListItemWithStatus } from '$common/room-list-search/searched-hall-list-item-with-status'
import { SearchSection } from '$common/room-list-search/search-section'
import {
  useGlobalSearch,
  useSearchedLocalRooms,
} from '$logic/room-list/room-list-search.react-hook'
import { openRoom } from '$logic/opened-room'
import { addToRecentSearch } from '$logic/room-list/room-list-search.service'
import { t } from '$i18n'
import { SearchedRoomListItemWithStatus } from './components/searched-room-list-item-with-status'
import { EmptySearchResult } from '$common/empty-search-result'

export const ChatListSearched = memo(function ChatListSearchedComponent({
  query,
}: {
  query: string
}) {
  const localSearch = useSearchedLocalRooms(query)
  const globalSearch = useGlobalSearch(query)

  const nonClientUserPvs = useMemo(() => {
    return globalSearch.pvIds?.filter(
      roomId => !localSearch.roomIds.includes(roomId),
    )
  }, [localSearch.roomIds, globalSearch.pvIds])

  const nonClientUserHalls = useMemo(() => {
    return globalSearch.userHalls?.filter(
      ({ roomId }) => !localSearch.roomIds.includes(roomId),
    )
  }, [localSearch.roomIds, globalSearch.userHalls])

  const nonClientUserHallRoomIds = useMemo(() => {
    return nonClientUserHalls.map(({ roomId }) => roomId)
  }, [nonClientUserHalls])

  const localRoomIds = useMemo(() => {
    return [
      ...localSearch.roomIds,
      ...nonClientUserPvs,
      ...nonClientUserHallRoomIds,
    ]
  }, [localSearch.roomIds, nonClientUserPvs, nonClientUserHallRoomIds])

  const localResultCount = localRoomIds.length

  const contacts = useMemo(() => {
    const pvUsersIds = globalSearch.pvUsers.map(({ id }) => id)
    return globalSearch.contacts?.filter(
      ({ id }) =>
        id && !(pvUsersIds.includes(id) || localSearch.pvUserIds.includes(id)),
    ) as (Contact & { id: string })[]
  }, [globalSearch.contacts, globalSearch.pvUsers, localSearch.pvUserIds])

  const globalUsers = useMemo(() => {
    return [...contacts, ...globalSearch.users]
  }, [contacts, globalSearch.users])

  const nonLocalHalls = useMemo(() => {
    return globalSearch.halls?.filter(
      ({ roomId }) => !localSearch.roomIds.includes(roomId),
    )
  }, [localSearch.roomIds, globalSearch.halls])

  const globalResultCount = globalUsers.length + nonLocalHalls.length

  const resultCount = localResultCount + globalResultCount

  if (resultCount === 0 && (globalSearch.loading || localSearch.loading))
    return <FullPageListSkeleton />

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      {!!localResultCount && (
        <Box sx={{ padding: '5px 0', backgroundColor: 'white' }}>
          {localRoomIds.map(roomId => (
            <SearchedRoomListItemWithStatus
              key={roomId}
              roomId={roomId}
              onClick={() => {
                openRoom(roomId)
                addToRecentSearch({ type: 'ROOM', id: roomId })
              }}
            />
          ))}
        </Box>
      )}

      {globalResultCount > 0 && (
        <>
          <SearchSection primaryText={t('chatListSearch.globals')} />
          <Box sx={{ padding: '5px 0', backgroundColor: 'white' }}>
            {globalUsers.map(({ id }) => (
              <SearchedUserListItemWithStatus key={id} userId={id} />
            ))}
            {nonLocalHalls.map(({ roomId }) => (
              <SearchedHallListItemWithStatus
                key={roomId}
                roomId={roomId}
                searchQuery={query}
                onClick={() => {
                  openRoom(roomId)
                  addToRecentSearch({ type: 'ROOM', id: roomId })
                }}
              />
            ))}
          </Box>
        </>
      )}

      {!resultCount && <EmptySearchResult />}
    </Box>
  )
})
